import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Popover } from '@mui/material';

import AgTreeView from '../../AgTreeView';
import { AgColor } from '../../../Styles/AgColor';
import AgBreadCrumbs, { BreadCrumbsProps } from '../../AgBreadCrumbs';

import { useAppDispatch } from '../../../../redux/hooks';
import { setAddNewOrgInfo as setAddNewOrgInfoStaff } from '../../../../redux/slices/staff';
import { setAddNewOrgInfo as setAddNewOrgInfoMember } from '../../../../redux/slices/member';
import { RenderTree, findAncestors, findChildrenById, findChildrenByName, findNodeById } from '../../../Utils/TreeUtils';

interface ExtendedRenderTree extends RenderTree {
  hasCh?: boolean; 
}
interface IntegratedComponentProps {
  data: ExtendedRenderTree;
  dataTestId?: string;
  getNodeChildren: Function;
  loadingId?: number;
}

const AgOrgNavigation: React.FC<IntegratedComponentProps> = ({ data, dataTestId, getNodeChildren, loadingId }) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const [anchors, setAnchors] = useState<Record<string, { pageX: number }>>({});
  const [currentChildren, setCurrentChildren] = useState<ExtendedRenderTree>(data);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isNodeClicked, setIsNodeClicked] = useState<boolean>();
  const [isRoot, setIsRoot] = useState<boolean>();
  
  const currentBreadcrumb: BreadCrumbsProps[] = JSON.parse(localStorage.getItem('orgBreadcrumb') || '{}');
  const newBreadcrumb = Array.from(currentBreadcrumb).find(b => b.name === 'Select');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadCrumbsProps[]>( !newBreadcrumb && !_.isEmpty(currentBreadcrumb) ? currentBreadcrumb : [
    { name: data?.name },
    { name: 'Select', id: 'select' }
  ]);
  const isMenuOpen = Boolean(currentIndex);

  useEffect(()=>{
    localStorage.setItem('orgBreadcrumb', JSON.stringify(breadcrumbs));
  }, [breadcrumbs]);

  const findCurr = (currentChildren: ExtendedRenderTree, breadcrumbs: BreadCrumbsProps[]) => {
    let result: RenderTree[] = [];
    result.push({ id: currentChildren?.id, name: currentChildren?.name });
    const findChildren = (currentChildren: ExtendedRenderTree) => {
      currentChildren?.children?.forEach(child => {
        // @ts-ignore
        if (child.children?.length){ // don't know if data is dirty, but there is org hasCh is false but children has value
          child.id && result.push({ id: child.id, name: child.name });
          findChildren(child);
        }else{
          child.id && result.push(child);
        }
      });
    };
    findChildren(currentChildren);
    return result.find(c => c.name === breadcrumbs[breadcrumbs?.length - 1]?.name);
  };

  useEffect(()=>{
    if(currentBreadcrumb.length === 2) {
      // const curr = currentChildren?.children?.find(c => c.name === breadcrumbs[breadcrumbs?.length - 1]?.name);
      const curr = findCurr(currentChildren, breadcrumbs);
      if(newBreadcrumb){ // only when length is 2 and there is newBreadcrumb, it's reset to original state
        dispatch(setAddNewOrgInfoStaff({ name: currentChildren?.name, id: currentChildren?.id }));
        dispatch(setAddNewOrgInfoMember({ name: currentChildren?.name, id: currentChildren?.id }));
      }else if(curr){
        dispatch(setAddNewOrgInfoStaff({ name: curr?.name, id: curr?.id }));
        dispatch(setAddNewOrgInfoMember({ name: curr?.name, id: curr?.id }));
      }
    }
  }, [breadcrumbs, currentBreadcrumb, currentChildren, dispatch, newBreadcrumb]);

  useEffect(() => {
    const f = async () => {
      setCurrentChildren(data);
      const selectedNode = findNodeById(data, currentChildren.id);
      const isNextNodeShowing = selectedNode?.children?.length! > 0 || false;

      if (!isNodeClicked && newBreadcrumb) {
        setBreadcrumbs([
          { name: data?.name },
          { name: 'Select', id: 'select' }
        ]);
      } else {
        if (isNextNodeShowing && !isRoot && selectedNode && selectedNode.hasCh) {
          // Don't push if the last item in the breadcrumb is 'Select'.
          setBreadcrumbs((prev) => {
            const isNotSelect = prev[prev.length - 1]?.id !== 'select';
            return [...prev, ...(isNotSelect ? [{ name: 'Select', id: 'select' }] : [])];
          });
        }
      }
      setIsRoot(false);
    };
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleNodeSelect = (nodeId: string | string[] | null) => {
    if (typeof nodeId === 'string') {
      const node = findNodeById(data, nodeId);

      dispatch(setAddNewOrgInfoMember({ name: node?.name?? '', id: node?.id?? '' })); // this info is used for 'add new' button
      dispatch(setAddNewOrgInfoStaff({ name: node?.name?? '', id: node?.id?? '' })); // this info is used for 'add new' button
      if (node) {
        setCurrentChildren(node);
      }
    }
  };

  const handleTreeViewNodeSelect = (nodeId: string | null) => {
    // Assuming the nodeName corresponds to nodeId, 
    // but you'll need to adjust if this isn't the case.
    setIsNodeClicked(true);
    handleNodeSelect(nodeId);
    handleMenuClose(nodeId);
  };

  const handleNodeExpandIconClicked = async (nodeId: string | null) => {
    await getNodeChildren(nodeId, false);
  };

  const handleMenuClose = (selectedItemId: string | null) => {
    selectedItemId && getNodeChildren(selectedItemId, true);
    setCurrentIndex(0);
    const childrenOfNode = findChildrenById(data, selectedItemId!);
    childrenOfNode && setCurrentChildren(childrenOfNode!);
    const node = findNodeById(data, selectedItemId!);
    if (selectedItemId) {
      const ancestors = findAncestors(data, selectedItemId);
      const ancestorNamesWithoutRoot = ancestors ? ancestors.map(node => node.name) : [];
      if (ancestorNamesWithoutRoot.length > currentIndex) {
        setBreadcrumbs([
          ...ancestorNamesWithoutRoot.map(item => {
            return {
              name: item,
            };
          })
        ]);
        return;
      }

      // Update breadcrumbs
      if (currentIndex === 0) {
        setBreadcrumbs([
          // ...breadcrumbs,
          {
            name: node?.name,
          }
        ]);
        return;
      }

      const currentBreadcrumbs = breadcrumbs.slice(0, currentIndex + 1);
      // currentBreadcrumbs[currentIndex-1].name = node?.name;
      const tempBreadcrumbs = [
        ...currentBreadcrumbs
      ];
      tempBreadcrumbs[currentIndex].name = node?.name;
      setBreadcrumbs(tempBreadcrumbs);
    }
  };
  
  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLElement> | undefined, name: string | undefined, index: number) => {
    if (index === 0) {
      setCurrentChildren(data || {} as ExtendedRenderTree);
      setBreadcrumbs([
        { name: data?.name },
        { name: 'Select', id: 'select',
        }
      ]);
      getNodeChildren(data.id, true);
      setCurrentIndex(!anchors[1]?.pageX ? 0 : 1);
      setIsRoot(true);
      return;
    }
    setIsRoot(false);
    setAnchors((prev) => ({ ...prev, [index]: { pageX: event?.pageX } }));
    setCurrentIndex(index);

    const parentName = breadcrumbs[index - 1].name;
    const children = findChildrenByName(data, parentName!);
    setCurrentChildren(children!);
  };

  // Set the org tree list based on the selected breadcrumb.
  useEffect(() => {
    if(!currentIndex) return;
    const parentName = breadcrumbs[currentIndex - 1]?.name;
    const children = findChildrenByName(data, parentName!);
    setCurrentChildren(children!);
  }, [breadcrumbs, currentIndex, data]);

  return (
    <div data-testid={dataTestId} style={{ flexGrow: 1, margin: '8px 16px 8px 215px' }}>
      <div style={{ display: 'flex' }}>
        <div style={{ color: AgColor.grey5 }}>
          {messages({ id: 'ORGANIZATION' })}
        </div>
        <div style={{ marginLeft: '20px' }}>
          <AgBreadCrumbs
            profileMenuItems={breadcrumbs}
            onBreadcrumbClick={handleBreadcrumbClick}
          />
        </div>
      </div>

      <Popover
        open={isMenuOpen}
        onClose={() => handleMenuClose(null)}
        anchorOrigin={{
          vertical: 33,
          horizontal: anchors?.[currentIndex]?.pageX || 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        PaperProps={{
          style: {
            width: '320px',
            overflow: 'auto'
          }
        }}
      >
        <div style={{
          maxWidth: '320px',
          overflowWrap: 'break-word'
        }}>
          <AgTreeView
            onNodeSelect={handleTreeViewNodeSelect}
            onNodeExpandIconClicked={handleNodeExpandIconClicked}
            data={currentChildren}
            breadcrumbs={breadcrumbs}
            resetBreadCrumbs={() => handleBreadcrumbClick(undefined, undefined, 0)}
            loadingId={loadingId}
          />
        </div>
      </Popover>
    </div>
  );
};

export default AgOrgNavigation;
